/** Standalone client-side analytics tracking utility.
 *  Import in any client component:
 *    import { trackEvent } from "@/lib/track";
 *    trackEvent("add_to_cart", { productId, price });
 */

const SESSION_KEY = "rw_session_id";
const VISITOR_KEY = "rw_visitor_id";
const USER_KEY = "rw_user_id";

/** Generate a UUID — fallback for older browsers without crypto.randomUUID (Chrome <92, Safari <15.4) */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback: crypto.getRandomValues is available in all modern browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> (c === "x" ? 0 : 2);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = generateUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

/** Persistent visitor ID that survives session resets — used for returning vs new */
export function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  let vid = localStorage.getItem(VISITOR_KEY);
  if (!vid) {
    vid = generateUUID();
    localStorage.setItem(VISITOR_KEY, vid);
  }
  return vid;
}

/** Called by Tracker component when session is available — stores userId for trackEvent() */
export function setTrackingUserId(userId: string | null) {
  if (typeof window === "undefined") return;
  if (userId) {
    sessionStorage.setItem(USER_KEY, userId);
  } else {
    sessionStorage.removeItem(USER_KEY);
  }
}

/**
 * Events that should fire even without analytics consent because they
 * are the consent mechanism itself (we need to know if users opted in
 * or out) or are essential for site functionality.
 */
const CONSENT_EXEMPT_EVENTS = new Set(["cookie_consent"]);

/** Fire a tracking event. Non-blocking, fire-and-forget.
 *
 * #13 — Respects the visitor's cookie preferences. If analytics consent
 * isn't granted, we skip the event entirely (except for the consent
 * event itself, which we need to record to prove compliance). Prevents
 * backend analytics from receiving data the user explicitly opted out
 * of.
 */
export function trackEvent(
  action: string,
  metadata?: Record<string, unknown>,
  overrides?: { page?: string; duration?: number }
) {
  if (typeof window === "undefined") return;

  // #13 — Consent gate. CONSENT_EXEMPT_EVENTS always fire.
  if (!CONSENT_EXEMPT_EVENTS.has(action)) {
    try {
      const stored = localStorage.getItem("cookie-preferences");
      const prefs = stored ? JSON.parse(stored) : null;
      if (prefs && prefs.analytics !== true) return;
      // No stored prefs means the banner hasn't been dismissed yet; we
      // default to NOT tracking until the user chooses (matches the
      // updated default in CookieConsent.tsx).
      if (!prefs) return;
    } catch {
      return; // localStorage unavailable → safer to skip
    }
  }

  const sid = getSessionId();
  if (!sid) return;

  const payload: Record<string, unknown> = {
    sessionId: sid,
    visitorId: getVisitorId(),
    userId: sessionStorage.getItem(USER_KEY) || null,
    action,
    page: overrides?.page || window.location.pathname,
    duration: overrides?.duration,
    metadata: metadata || undefined,
  };

  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      "/api/track",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );
  } else {
    fetch("/api/track", {
      method: "POST",
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }
}
